import type { Cell, Direction } from './types';

export const GRID_SIZE = 50;

// The workspace is the rounded "blackboard" area inside the purple frame.
// Top-left of grid cell (0,0). Side margins must be wide enough to fit ports
// (porthole radius 40 + cone length 44 ≈ 84px) plus a little breathing room.
export const WORKSPACE = {
  originX: 100,
  originY: 100,
  cols: 20,
  rows: 16,
};

export const WORKSPACE_WIDTH = WORKSPACE.cols * GRID_SIZE;
export const WORKSPACE_HEIGHT = WORKSPACE.rows * GRID_SIZE;

// Right-side molecule legend panel (sits ABOVE the organ pool sidebar — they're
// two visually-separate rounded boxes so the legend doesn't eat pool real-estate).
export const LEGEND = {
  x: WORKSPACE.originX + WORKSPACE_WIDTH + 120, // 120 = breathing room for right-wall ports
  y: 88, // just below the top bar (which is y 0–80)
  width: 180,
  height: 120,
};

// Right-side organ pool sidebar. Starts below the legend panel and extends down
// toward the bottom of the screen — needs to fit up to 6 organs for Level 11.
export const SIDEBAR = {
  x: WORKSPACE.originX + WORKSPACE_WIDTH + 120,
  y: LEGEND.y + LEGEND.height + 8,
  width: 180,
  height: 800, // ends at y ≈ 1016, just inside GAME_HEIGHT (1030)
};

// Game logical resolution
export const GAME_WIDTH = SIDEBAR.x + SIDEBAR.width + 30; // 1330
export const GAME_HEIGHT = WORKSPACE.originY + WORKSPACE_HEIGHT + 130; // 1030

export function gridToWorld(cell: Cell): { x: number; y: number } {
  return {
    x: WORKSPACE.originX + cell.col * GRID_SIZE + GRID_SIZE / 2,
    y: WORKSPACE.originY + cell.row * GRID_SIZE + GRID_SIZE / 2,
  };
}

export function worldToGrid(x: number, y: number): Cell {
  return {
    col: Math.floor((x - WORKSPACE.originX) / GRID_SIZE),
    row: Math.floor((y - WORKSPACE.originY) / GRID_SIZE),
  };
}

export function isInsideGrid(cell: Cell): boolean {
  return cell.col >= 0 && cell.col < WORKSPACE.cols && cell.row >= 0 && cell.row < WORKSPACE.rows;
}

export function cellEquals(a: Cell, b: Cell): boolean {
  return a.col === b.col && a.row === b.row;
}

export function cellAreAdjacent(a: Cell, b: Cell): boolean {
  const dc = Math.abs(a.col - b.col);
  const dr = Math.abs(a.row - b.row);
  return dc + dr === 1;
}

export function step(cell: Cell, dir: Direction): Cell {
  switch (dir) {
    case 'N':
      return { col: cell.col, row: cell.row - 1 };
    case 'S':
      return { col: cell.col, row: cell.row + 1 };
    case 'E':
      return { col: cell.col + 1, row: cell.row };
    case 'W':
      return { col: cell.col - 1, row: cell.row };
  }
}
